// chat/actions.ts — the chat control executor (cloned from Oriora Expense's src/chat/actions.ts,
// adapted to THIS app's store — Round Card). The client router (chat/brain.ts) maps ONE user
// message → ONE ChatAction; this file EXECUTES that action against the on-device store
// (src/store.ts). Everything here is LOCAL SQLite over Tauri IPC — no network, no billing —
// EXCEPT `recap`, which reuses the app's ONE existing live endpoint (recapSeason) exactly as the
// InsightsTab does. No new server routes.
//
// HITL discipline (mirrors Expense): the destructive action (delete_round) is NEVER executed from a
// chat turn on its own — the Chat UI holds it and requires an explicit Confirm tap first.

import {
  store, todayLocal, holesLabel, fairwayPct, fmtShortDate, seasonGlance, estimateHandicap,
  type NewRound, type RoundPatch, type NewBagChange, type Round,
} from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "round-card";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { recapSeason, fenceCheck, BrainError, FenceError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact round row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  title: string; // course
  sub: string;   // date
  meta: string;  // score · stats · conditions
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_round"; input: NewRound; label?: string }
  | { kind: "edit_round"; id: string; patch: RoundPatch; label?: string }
  | { kind: "delete_round"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "add_bag"; input: NewBagChange; label?: string }
  | { kind: "delete_bag_change"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "filter"; pred: (r: Round) => boolean; label?: string }
  | { kind: "recap" }      // reuses the existing recapSeason endpoint (the ONE billed call)
  | { kind: "summarize" } // local, deterministic, free
  | { kind: "handicap" }  // estimated handicap index, computed on-device — free
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;    // shown back in the chat
  changed?: boolean;  // the archive changed → the main list should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

function rowMeta(r: Round): string {
  const bits: string[] = [`${r.total_score} over ${holesLabel(r.holes_played)}`];
  if (r.fairways_hit !== null && r.fairways_eligible !== null) bits.push(`FW ${fairwayPct(r.fairways_hit, r.fairways_eligible)}`);
  if (r.greens_in_reg !== null) bits.push(`GIR ${r.greens_in_reg}`);
  if (r.putts !== null) bits.push(`${r.putts} putts`);
  if (r.conditions) bits.push(r.conditions);
  return bits.join(" · ");
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_round": {
        const r = await store.createRound(a.input);
        return { ok: true, message: `Logged ${r.course} — ${r.total_score} over ${holesLabel(r.holes_played)}.`, changed: true };
      }

      case "edit_round":
        await store.updateRound(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the round"}.`, changed: true };

      case "delete_round":
        await store.deleteRound(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the round"}.`, changed: true };

      case "add_bag": {
        const b = await store.createBagChange(a.input);
        const retired = b.action === "retired";
        return { ok: true, message: `${retired ? "Retired" : "Added"} ${b.club} ${retired ? "from" : "to"} your bag.`, changed: true };
      }

      case "delete_bag_change":
        await store.deleteBagChange(a.id);
        return { ok: true, message: `Removed the bag change for ${a.label ?? "that club"}.`, changed: true };

      case "filter": {
        const all = await store.listRounds({ limit: 50000 });
        const rows = all.filter(a.pred);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        const rendered: FilterRow[] = rows.slice(0, 50).map((r) => ({
          id: r.id, title: r.course || "Unnamed course", sub: fmtShortDate(r.played_on), meta: rowMeta(r),
        }));
        return {
          ok: true,
          message: `${rows.length} match${rows.length !== 1 ? "es" : ""}${forLabel}` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rendered,
        };
      }

      case "recap": {
        // Reuses the app's ONE live endpoint (recapSeason) — same path as InsightsTab.
        const rounds = await store.listRounds({ limit: 50000 });
        if (rounds.length === 0) return { ok: true, message: "Log a round first — there's nothing to recap yet." };
        // PRE-LLM fence (defence-in-depth): screen the corpus' free text (course names / notes) so a
        // coaching- or handicap-shaped string never slips toward the brain. The hard refusal of the
        // user's ASK is already handled in the router; this guards the derived corpus too.
        const corpus = rounds.map((r) => `${r.course} ${r.notes}`).join(" ");
        const v = fenceCheck(corpus);
        if (!v.ok) return { ok: true, message: v.message };
        const sig = await store.computeSignals(todayLocal());
        const r = await recapSeason(sig);
        const recap = r.recap.trim();
        return { ok: true, message: recap || "Not enough logged rounds to recap yet — log a few more." };
      }

      case "summarize": {
        const rounds = await store.listRounds({ limit: 50000 });
        const g = seasonGlance(rounds);
        if (g.totalRounds === 0) return { ok: true, message: "No rounds logged yet — log your first to start your scorecard archive." };
        const bits: string[] = [`${g.totalRounds} round${g.totalRounds !== 1 ? "s" : ""}`];
        if (g.avgPer18 !== null) bits.push(`avg ${g.avgPer18}/18`);
        if (g.bestPer18 !== null) bits.push(`best ${g.bestPer18}`);
        if (g.index !== null) bits.push(`est. index ${g.index > 0 ? "+" : ""}${g.index} vs par`);
        if (g.lastPlayed) bits.push(`last played ${fmtShortDate(g.lastPlayed)}`);
        return { ok: true, message: bits.join(" · ") + "." };
      }

      case "handicap": {
        const rounds = await store.listRounds({ limit: 50000 });
        const h = estimateHandicap(rounds);
        if (h.index === null) {
          return { ok: true, message: "I can't estimate a handicap yet — log at least a few 18-hole rounds WITH the course rating and slope (from the scorecard, per tee), and I'll compute it." };
        }
        return { ok: true, message: `Your estimated handicap index is ${h.index}, from your best differentials across ${h.used} qualifying round${h.used !== 1 ? "s" : ""}. (An observational estimate — not an official/certified handicap.)` };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my rounds" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole rounds collection). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    if (e instanceof BrainError) return { ok: false, message: e.message };
    if (e instanceof FenceError) return { ok: false, message: e.message };
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
